import React, { useCallback, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import loadable from 'components/utils/lazyImport'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import NavigationBar from './NavigableOfferContentBar'
import PropertyReview from './PropertyReview'
import LayoutContainer from 'components/Common/LayoutContainer'
import { PROPERTY_MAP_MARKER_TYPE } from 'constants/map'
import { scrollToId } from 'lib/window/scrollUtils'
import FinePrint from '../../Common/FinePrint'
import TraderInformation from './TraderInformation'
import BodyText from 'components/Luxkit/Typography/BodyText'
import NavigableOfferContentGroup from './NavigableOfferContentGroup'
import Modal from 'components/Luxkit/Modal/Modal'
import TextLink from 'components/Luxkit/TextLink'
import UserReviewList from './UserReviewList'
import { isBundleOffer } from 'lib/offer/offerTypes'
import Group from 'components/utils/Group'

const PropertyMap = loadable(() => import(/* webpackChunkName: "PropertyMap" */ 'components/Common/PropertyMap'))

interface Props {
  hash?: string;
  offerDetails: App.Offer;
  traderInformation?: App.TraderInformation;
  mobileHotelOfferPageTest?: boolean;
}

const ContentWrapper = styled(LayoutContainer)`
  > * {
    margin-top: ${rem(24)};
    padding-top: ${rem(24)};

    & + * {
    border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
  }
  }
`

const StyledFacilities = styled(MarkdownRender)`
  > ul {
    columns: auto 2;
  }
`

function packageHasPropertyWithCoords(offer: App.Offer) {
  return !!offer.property?.latitude && !!offer.property?.longitude
}

interface ContentField {
  id: string;
  label: string;
  open: boolean;
  initialHeight?: number;
  minimumContentHeight?: number;
  contentComponent: React.ReactElement | Array<React.ReactElement>;
}

function getContentFields(props: Props): Array<ContentField> {
  const {
    hash,
    offerDetails,
    mobileHotelOfferPageTest,
  } = props

  const {
    highlights,
    facilities,
    finePrint,
    gettingThere,
    lowestPricePackage,
    property,
  } = offerDetails

  const contentFields = []
  if (packageHasPropertyWithCoords(offerDetails)) {
    const markers: Array<App.MapMarker> = []

    if (isBundleOffer(offerDetails)) {
      const bundleOffers = Object.values(offerDetails.bundledOffers)
      bundleOffers.forEach((bundleOffer) => {
        markers.push({
          id: bundleOffer.property.id,
          type: PROPERTY_MAP_MARKER_TYPE,
          latitude: bundleOffer.property.latitude,
          longitude: bundleOffer.property.longitude,
        })
      })
    } else {
      if (property?.latitude && property?.longitude) {
        markers.push({
          id: property.id,
          type: PROPERTY_MAP_MARKER_TYPE,
          latitude: property.latitude,
          longitude: property.longitude,
        })
      }
    }

    const bundledOffers = Object.values(offerDetails.bundledOffers ?? {})

    contentFields.push({
      id: 'getting-there',
      label: 'Location',
      open: hash === '#getting-there',
      initialHeight: 410,
      contentComponent: <>
        {isBundleOffer(offerDetails) && <Group gap={16} direction="vertical">
          <div>
            <BodyText variant="large">{bundledOffers[0].property?.name}</BodyText>
            <BodyText variant="large" weight="bold">{bundledOffers[0].property?.address}</BodyText>
          </div>
          <div>
            <BodyText variant="large">{bundledOffers[1].property?.name}</BodyText>
            <BodyText variant="large" weight="bold">{bundledOffers[1].property?.address}</BodyText>
          </div>
        </Group>}
        {!isBundleOffer(offerDetails) && <div>
          <BodyText variant="large">{property?.name}</BodyText>
          <BodyText variant="large" weight="bold">{property?.address}</BodyText>
        </div>}
        <Group gap={16} direction="vertical">
          <PropertyMap
            height={258}
            wrapperStyle={{ height: 258 }}
            markers={markers}
          />
          <MarkdownRender type="compact" content={gettingThere}/>
        </Group>
      </>,
    })
  }

  if (highlights && !mobileHotelOfferPageTest) {
    contentFields.push({
      id: 'highlights',
      label: 'Highlights',
      contentComponent: <MarkdownRender type="compact" fontSize="large" content={highlights} />,
      open: hash === '#highlights',
    })
  }

  if (facilities && !mobileHotelOfferPageTest) {
    contentFields.push({
      id: 'facilities',
      label: 'Facilities',
      open: hash === '#facilities',
      initialHeight: 120,
      contentComponent: <StyledFacilities type="compact" fontSize="large" content={facilities} />,
    })
  }

  if (gettingThere && !packageHasPropertyWithCoords(offerDetails)) {
    contentFields.push({
      id: 'getting-there',
      label: 'Location',
      open: hash === '#getting-there',
      contentComponent: <MarkdownRender type="compact" fontSize="large" content={gettingThere} />,
    })
  }

  if (lowestPricePackage?.tour?.itinerary) {
    contentFields.push({
      id: 'itinerary',
      label: 'Itinerary',
      open: hash === '#itinerary',
      contentComponent: <MarkdownRender type="compact" fontSize="large" content={lowestPricePackage.tour.itinerary} />,
    })
  }

  if (finePrint) {
    contentFields.push({
      id: 'fine-print',
      label: 'Fine Print',
      contentComponent: <FinePrint finePrint={finePrint} offer={offerDetails}/>,
      open: !!offerDetails.property?.taxesAndFeesContent || hash === '#fine-print',
    })
  }

  if (lowestPricePackage?.tour?.reviews.length) {
    contentFields.push({
      id: 'customer-reviews',
      label: 'Reviews',
      open: hash === '#customer-reviews',
      contentComponent: lowestPricePackage.tour.reviews.map(review =>
        <PropertyReview
          key={review.id} content={review.content}
          source={review.source}
        />,
      ),
    })
  }

  if (!isBundleOffer(offerDetails) && property?.rating?.reviewsTotal && property?.rating.score) {
    contentFields.push({
      id: 'customer-reviews',
      label: 'Reviews',
      open: hash === '#customer-reviews',
      initialHeight: 200,
      minimumContentHeight: 2000,
      contentComponent: <UserReviewList property={property} />,
    })
  }

  return contentFields
}

function NavigableOfferContent(props: Props) {
  const contentFields = getContentFields(props)
  const defaultOpenField = contentFields.find(field => field.open)
  const [openFields, setOpenFields] = useState<Record<string, boolean>>({ [defaultOpenField?.id]: true })
  const [headerSelected, setHeaderSelected] = useState<string>(defaultOpenField?.id)
  const [modalOpen, setModalOpen] = useState<boolean>(false)

  const onGroupInView = (id: string, inView: boolean) => {
    if (inView) {
      setHeaderSelected(id)
    }
  }

  const setFieldToggle = useCallback((id: string, toggled: boolean) => {
    window.setTimeout(() => {
      scrollToId(id, -54)
    }, 300)

    setOpenFields({
      ...openFields,
      [id]: toggled,
    })
  }, [openFields])

  const onHeaderClick = useCallback((id: string) => {
    setFieldToggle(id, true)
  }, [setFieldToggle])

  const onReadMoreToggle = useCallback((id: string) => {
    setFieldToggle(id, !openFields[id])
  }, [setFieldToggle, openFields])

  const onModalOpenToggle = useCallback(() => {
    setModalOpen(!modalOpen)
  }, [setModalOpen, modalOpen])

  return (
    <>
      <NavigationBar
        contentFields={contentFields}
        onClick={onHeaderClick}
        selectedHeader={headerSelected}
      />
      <ContentWrapper size="md">
        {contentFields.map(contentField => <NavigableOfferContentGroup
          key={contentField.id}
          heading={contentField.label}
          id={contentField.id}
          onInViewChange={onGroupInView}
          initialHeight={contentField.initialHeight ?? 96}
          minimumContentHeight={contentField.minimumContentHeight ?? 0}
          open={!!openFields[contentField.id]}
          onToggle={onReadMoreToggle}
        >
          <BodyText variant="large">
            {contentField.contentComponent}
          </BodyText>
        </NavigableOfferContentGroup>,
        )}
        {props.traderInformation && (
          <div id="trader-information">
            <TextLink size="medium" onClick={onModalOpenToggle}>
              About this supplier
            </TextLink>
            <Modal
              title="About this supplier"
              isOpen={modalOpen}
              onClose={onModalOpenToggle}
            >
              <TraderInformation traderInformation={props.traderInformation} />
            </Modal>
          </div>
        )}
      </ContentWrapper>
    </>
  )
}

export default NavigableOfferContent
